/**
 * session/care.ts — the thin, pure glue between the frozen {@link CareState} side-table
 * (keyed by a mon's stable instance id) and the pure rules in `core/care.ts`. GameSession
 * owns persistence + the live PokemonInstance; this module only computes the next immutable
 * CareState (upsert a record, ease fullness overnight) so those operations stay testable and
 * the session verbs read as one-liners.
 *
 * Happiness is NOT stored here — it IS `instance.friendship` (already persisted). This table
 * carries only the soft, un-punishing extras: fullness, when it was last cared for, and a worn
 * cosmetic. Every helper returns a fresh CareState (never mutates the input array).
 */
import type { CareState, FollowerCareRecord } from '@/shared';
import { fullnessAfterAbsence, applyCare, type CareAction, type CareOutcome } from '@/core';

/** A freshly-met partner starts comfortably above the resting floor — never hungry. */
export const CARE_START_FULLNESS = 75;

/** The existing care record for a mon, or undefined when it has never been cared for. */
export function getCareRecord(
  state: CareState,
  instanceId: string,
): FollowerCareRecord | undefined {
  return state.records.find((r) => r.instanceId === instanceId);
}

/** Upsert a record into the table by instance id, returning a NEW state (input untouched). */
export function upsertCareRecord(state: CareState, rec: FollowerCareRecord): CareState {
  const others = state.records.filter((r) => r.instanceId !== rec.instanceId);
  return { records: [...others, rec] };
}

/**
 * The record for a mon, lazily created (at {@link CARE_START_FULLNESS}) when absent. Returns
 * the (possibly new) record AND the state that contains it — so a first read persists a warm
 * starting point rather than re-defaulting every time.
 */
export function ensureCareRecord(
  state: CareState,
  instanceId: string,
  now: number,
): { state: CareState; record: FollowerCareRecord } {
  const existing = getCareRecord(state, instanceId);
  if (existing) return { state, record: existing };
  const record: FollowerCareRecord = {
    instanceId,
    fullness: CARE_START_FULLNESS,
    lastCaredAt: now,
    cosmeticId: null,
  };
  return { state: upsertCareRecord(state, record), record };
}

/**
 * Compute one care action's result for a mon whose current friendship is `friendship` and
 * whose care record is `record`. Returns the pure {@link CareOutcome} (deltas the scene
 * animates) plus the NEXT care record (fullness eased up, `lastCaredAt` stamped). The caller
 * writes `outcome.friendship` onto the live instance and upserts the record.
 */
export function careActOn(
  record: FollowerCareRecord,
  friendship: number,
  action: CareAction,
  now: number,
): { outcome: CareOutcome; record: FollowerCareRecord } {
  const outcome = applyCare(action, { friendship, fullness: record.fullness });
  return {
    outcome,
    record: { ...record, fullness: outcome.fullness, lastCaredAt: now },
  };
}

/**
 * Overnight easing: ease every record's fullness toward the comfortable floor by the elapsed
 * days (clamped at the floor, never below — the anti-DW1 contract). Friendship is untouched
 * (it only ever grows). A same-day / zero-day return is a no-op. Returns a fresh CareState.
 */
export function easeCareForAbsence(state: CareState, elapsedDays: number): CareState {
  if (elapsedDays <= 0 || state.records.length === 0) return state;
  return {
    records: state.records.map((r) => ({
      ...r,
      fullness: fullnessAfterAbsence(r.fullness, elapsedDays),
    })),
  };
}

/** Wear a cosmetic on a mon's care record (lazily creating the record). Returns a new state. */
export function wearCosmetic(
  state: CareState,
  instanceId: string,
  cosmeticId: string,
  now: number,
): CareState {
  const { state: withRec, record } = ensureCareRecord(state, instanceId, now);
  return upsertCareRecord(withRec, { ...record, cosmeticId });
}
